import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo } from "react";
import { DataTable } from "@/components/custom/table/data-table";
import { DataTableContent } from "@/components/custom/table/data-table-content";
import { DataTablePagination } from "@/components/custom/table/data-table-pagination";
import type { Tool } from "../types";

// Columns used for filtering only, always hidden
const HIDDEN_FILTER_COLUMNS: VisibilityState = {
  authProviders: false,
  secrets: false,
};

type ToolsTableProps = {
  data: Tool[];
  columns: ColumnDef<Tool>[];
  defaultPageSize?: number;
  stickyColumnBg?: "card" | "background";
  // Hide toolkit and version columns (used in grouped card view)
  hideToolkitColumn?: boolean;
  // Controlled column visibility
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (
    updater: VisibilityState | ((old: VisibilityState) => VisibilityState)
  ) => void;
};

export const ToolsTable = ({
  data,
  columns,
  defaultPageSize = 10,
  stickyColumnBg = "background",
  hideToolkitColumn = false,
  columnVisibility: externalColumnVisibility,
  onColumnVisibilityChange,
}: ToolsTableProps) => {
  // Merge external visibility with always-hidden filter columns
  const mergedColumnVisibility = useMemo<VisibilityState>(
    () => ({
      ...externalColumnVisibility,
      ...HIDDEN_FILTER_COLUMNS,
      ...(hideToolkitColumn && { toolkit: false, version: false }),
    }),
    [externalColumnVisibility, hideToolkitColumn]
  );

  return (
    <DataTable
      className="my-2 space-y-4"
      columns={columns}
      columnVisibility={mergedColumnVisibility}
      data={data}
      defaultPageSize={defaultPageSize}
      onColumnVisibilityChange={onColumnVisibilityChange}
    >
      <DataTableContent stickyColumnBg={stickyColumnBg} />
      <DataTablePagination />
    </DataTable>
  );
};
